import { useCallback, useEffect, useState } from "react";
import { Box, Button, CircularProgress, Collapse, Paper, Stack, Typography } from "@mui/material";

import { callApi } from "@/dataprovider/misc-apis";
import {
  LEAVE_BALANCE_FIELD_LABELS,
  deriveLeaveBalancePlainSummary,
  formatLeaveBalanceDetailValue,
  isLeaveBalanceEndpointMissing,
  leaveBalanceApiLocation,
  parseLeaveBalancePayload,
  type LeaveBalanceFetchStatus,
} from "@/containers/staff/shared/leave-balance";

type Props = {
  employeeId: number;
  /** Office staff dashboard vs personal My dashboard. Default office. */
  mode?: "office" | "self";
};

/**
 * FY leave balance card — plain-language summary first, numbers under “Show details”.
 */
export function StaffLeaveBalanceCard({ employeeId, mode = "office" }: Props) {
  const [state, setState] = useState<LeaveBalanceFetchStatus>({ status: "loading" });
  const [detailsOpen, setDetailsOpen] = useState(false);

  const load = useCallback(async () => {
    if (mode === "office" && !employeeId) {
      setState({ status: "unavailable" });
      return;
    }
    setState({ status: "loading" });
    try {
      const { data } = await callApi({
        location: leaveBalanceApiLocation(mode, employeeId),
        method: "GET",
      });
      const parsed = parseLeaveBalancePayload(data);
      if (!parsed) {
        setState({ status: "unavailable", reason: "Unexpected balance response" });
        return;
      }
      if (!parsed.enabled) {
        setState({ status: "disabled", financialYear: parsed.financialYear });
        return;
      }
      setState({ status: "ready", balance: parsed });
    } catch (err) {
      if (isLeaveBalanceEndpointMissing(err)) {
        setState({ status: "unavailable", reason: "Leave balance is not available yet" });
        return;
      }
      setState({ status: "unavailable", reason: "Could not load leave balance" });
    }
  }, [employeeId, mode]);

  useEffect(() => {
    void load();
  }, [load]);

  if (state.status === "unavailable") {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
          Leave balance
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {state.reason ?? "Leave balance is not available."}
        </Typography>
      </Paper>
    );
  }

  if (state.status === "disabled") {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
          Leave balance
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Paid leave policy is not enabled
          {state.financialYear ? ` for FY ${state.financialYear}` : ""}. Configure it in Staff
          settings when ready.
        </Typography>
      </Paper>
    );
  }

  if (state.status === "loading") {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <CircularProgress size={22} />
          <Typography variant="body2" color="text.secondary">
            Loading leave balance…
          </Typography>
        </Stack>
      </Paper>
    );
  }

  const { balance } = state;
  const summary = deriveLeaveBalancePlainSummary(balance);

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="baseline"
        justifyContent="space-between"
        flexWrap="wrap"
        useFlexGap
        sx={{ mb: 1 }}
      >
        <Typography variant="subtitle1" fontWeight={600}>
          Leave balance
        </Typography>
        {balance.financialYear ? (
          <Typography variant="body2" color="text.secondary">
            FY {balance.financialYear}
          </Typography>
        ) : null}
      </Stack>

      <Typography variant="body1" fontWeight={600} sx={{ mb: 1 }}>
        {summary.headline}
      </Typography>

      <Stack spacing={0.5} sx={{ mb: 1 }}>
        {summary.supportingLines.map((line) => (
          <Typography key={line} variant="body2" color="text.secondary">
            {line}
          </Typography>
        ))}
      </Stack>

      <Button
        size="small"
        variant="outlined"
        onClick={() => setDetailsOpen((open) => !open)}
        sx={{ mb: detailsOpen ? 1 : 0 }}
      >
        {detailsOpen ? "Hide details" : "Show details"}
      </Button>

      <Collapse in={detailsOpen}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)" },
            gap: 1.5,
            pt: 0.5,
          }}
        >
          {LEAVE_BALANCE_FIELD_LABELS.map(({ key, label }) => (
            <Box key={key}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                {label}
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {formatLeaveBalanceDetailValue(key, balance)}
              </Typography>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Paper>
  );
}
